import {
  DEFAULT_STROKE_OPTIONS,
  BRUSH_PRESETS,
  connectorGeometry,
  nodeAABB,
  rectCenter,
  rectExpand,
  rectIntersects,
  selectionBounds,
  selectionRotation,
  averagePressure,
  strokeCenterline,
  strokeOutline,
  vDist,
  visibleRect,
  type BoardMeta,
  type BoardNode,
  type Rect,
  type Viewport,
} from '@kanvas/board-core';
import type { BoardDoc } from '../collab/BoardDoc';
import { editorState, useEditor } from '../state/store';
import {
  boardLayerEpoch,
  pruneLasers,
  resetScratch,
  scratch,
  setInvalidator,
} from '../state/scratch';
import { animations } from './animations';
import { paintNode, type PaintEnv } from './drawNode';
import { paintGrid } from './grid';
import { measureGeneration } from './measure';
import { PeerCursorLayer } from './peerCursors';
import { PeerDraftLayer } from './peerDrafts';
import type { SelectionFrame } from './handles';
import {
  paintAnchors,
  paintBrushCursor,
  paintConnectorGrips,
  paintConnectorPreview,
  paintCreationPreview,
  paintCropOverlay,
  paintEraser,
  paintGuides,
  paintHandles,
  paintHighlight,
  paintHover,
  paintLasers,
  paintMarquee,
  paintMemberOutlines,
  paintPresenceViewports,
  paintSelectionOutline,
} from './overlay';

/**
 * How long after the board arrives to keep treating incoming nodes as part of
 * that arrival rather than as edits.
 *
 * Measured from whichever came later, the renderer starting or the first sync
 * completing. A plain timer from mount was not enough: a slow connection can
 * deliver the whole board after the window has already passed, and every
 * object in it then animates as though a peer had just made it.
 */
const SETTLE_MS = 400;

/**
 * Frame pacing at which the board gives up the expensive chrome, and the
 * pacing at which it takes it back.
 *
 * The gap between the two is deliberate: a single threshold would have the
 * glass flickering on and off for anyone whose machine sits right on it.
 */
const SLOW_FRAME_MS = 26;
const FAST_FRAME_MS = 15;

/** Minimum time a quality decision stands before it may be revisited. */
const QUALITY_HOLD_MS = 4000;

/** Longest in-progress stroke still painted as a full pressure outline. */
const LIVE_OUTLINE_MAX_POINTS = 2400;

/**
 * Single-canvas renderer driven by an explicit invalidate/rAF loop.
 *
 * There is deliberately no React in the paint path. React decides *what* the UI
 * chrome looks like; the canvas is repainted imperatively whenever the document,
 * the camera or the transient interaction state changes. That is the only way
 * to keep a 60 Hz drag smooth once a board holds a few thousand nodes.
 */
export class BoardRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private doc: BoardDoc;
  private frame = 0;
  private dpr = 1;
  private disposers: (() => void)[] = [];
  private destroyed = false;

  /** Last painted frame time, used by the laser fade. */
  private lastPaint = 0;

  /**
   * Smoothed interval between frames of a continuous repaint, and the quality
   * decision it drives.
   *
   * There is no way to ask a browser how fast it composites, and the browsers
   * this matters for are the ones no feature query distinguishes — they all
   * report support for the effects that are slow on them. So the renderer
   * measures itself instead: if it cannot hold a frame rate while something is
   * actually moving, it takes the cost out of the chrome rather than out of the
   * board. See `html[data-board-quality='low']` in styles.css.
   */
  private frameEma = 16.7;
  private qualityLow = false;
  private qualityHoldUntil = 0;

  /** Smoothed remote cursors and remote selections. */
  private peerCursors = new PeerCursorLayer();

  /** Strokes peers are drawing right now, smoothed the same way. */
  private peerDrafts = new PeerDraftLayer();

  /**
   * The board itself — background, grid and every node — as painted by the
   * last frame that had to paint it, plus everything that frame depended on.
   *
   * Most frames change nothing on the board: the brush cursor follows the pen,
   * a stroke is being drawn, a peer's cursor glides, the hover outline moves.
   * Repainting every node for those made a board full of handwriting and text
   * lag in proportion to how much was on it. Such frames now copy this bitmap
   * and paint only the live layers on top.
   */
  private boardLayer: HTMLCanvasElement | null = null;
  private boardInputs: unknown[] = [];

  /**
   * When this renderer started. An animation is only informative when it marks
   * a change someone just made, so nothing animates until the board has
   * settled — see `SETTLE_MS`.
   */
  private readonly createdAt = performance.now();

  constructor(canvas: HTMLCanvasElement, doc: BoardDoc) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D is unavailable');
    this.ctx = ctx;
    this.doc = doc;

    this.invalidate = this.invalidate.bind(this);
    setInvalidator(this.invalidate);

    this.disposers.push(doc.subscribe(this.invalidate));
    this.disposers.push(doc.subscribePresence(this.invalidate));
    this.disposers.push(
      doc.onStructureChange((change) => {
        const now = performance.now();
        if (now - Math.max(this.createdAt, doc.syncedAt ?? 0) < SETTLE_MS) return;
        const measure = (node: BoardNode) =>
          node.type === 'connector' ? connectorLength(node, doc) : 240;

        // Objects a peer created are replayed as motion so you can see what
        // they did; your own get a short confirmation instead. The exception is
        // a stroke we watched them draw live through presence — that ink never
        // left the screen, so replaying it would look like the board dropping
        // their stroke and drawing it again.
        const replay: BoardNode[] = [];
        const settled: BoardNode[] = [];
        for (const node of change.added) {
          const watched =
            !change.local &&
            node.type === 'draw' &&
            this.peerDrafts.wasDrawing(node.createdBy, now);
          (watched ? settled : replay).push(node);
        }
        animations.enter(replay, !change.local, measure);
        animations.enter(settled, false, measure);

        // The eraser rewrites strokes by remove+add. Fading out the old stroke
        // on top of the remnants makes the ink look thicker and shadowed.
        const eraserActive = editorState().tool === 'eraser';
        animations.exit(
          eraserActive
            ? change.removed.filter((n) => n.type !== 'draw')
            : change.removed,
        );
        this.invalidate();
      }),
    );
    // Camera, tool and selection changes all repaint; the store is a single
    // subscription because a shallow diff here is cheaper than many selectors.
    this.disposers.push(editorSubscribe(this.invalidate));
  }

  resize(width: number, height: number, dpr: number): void {
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.round(width * dpr));
    this.canvas.height = Math.max(1, Math.round(height * dpr));
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.invalidate();
  }

  invalidate(): void {
    if (this.destroyed || this.frame !== 0) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.render();
    });
  }

  /**
   * Lowers or restores render quality, no more than once per `QUALITY_HOLD_MS`.
   */
  private reviewQuality(now: number): void {
    if (now < this.qualityHoldUntil) return;
    if (this.qualityLow ? this.frameEma < FAST_FRAME_MS : this.frameEma > SLOW_FRAME_MS) {
      this.qualityLow = !this.qualityLow;
      this.qualityHoldUntil = now + QUALITY_HOLD_MS;
      this.applyQuality();
    }
  }

  private applyQuality(): void {
    const root = document.documentElement;
    if (this.qualityLow) root.dataset.boardQuality = 'low';
    else delete root.dataset.boardQuality;
  }

  destroy(): void {
    this.destroyed = true;
    this.qualityLow = false;
    this.applyQuality();
    if (this.frame) cancelAnimationFrame(this.frame);
    for (const d of this.disposers) d();
    this.disposers = [];
    animations.clear();
    this.peerCursors.clear();
    this.peerDrafts.clear();
    resetScratch();
    setInvalidator(() => {});
  }

  // -------------------------------------------------------------------------

  render(): void {
    const { ctx } = this;
    const state = editorState();
    const vp = state.viewport;
    const meta = this.doc.getMeta();
    const now = performance.now();
    const sinceLastPaint = now - this.lastPaint;
    this.lastPaint = now;

    // Only frames from a continuous run say anything about how fast this board
    // paints. The gap after the board has been sitting idle is not a dropped
    // frame, and counting it would degrade quality the moment anyone came back
    // to a board they had left alone.
    if (sinceLastPaint > 0 && sinceLastPaint < 120) {
      this.frameEma += (sinceLastPaint - this.frameEma) * 0.1;
      this.reviewQuality(now);
    }

    // ---- document ---------------------------------------------------------
    const nodes = this.doc.all();
    const inputs: unknown[] = [
      // A fresh array whenever any node changes, synchronously — unlike the
      // document version, which only bumps on the next frame.
      nodes,
      vp.x,
      vp.y,
      vp.zoom,
      vp.width,
      vp.height,
      this.dpr,
      this.canvas.width,
      this.canvas.height,
      meta.background,
      meta.grid,
      meta.gridSize,
      state.showGrid,
      state.editingId,
      state.croppingId,
      state.enteredNotebookId,
      boardLayerEpoch(),
      measureGeneration(),
    ];
    const animating = animations.active(now);
    // Cropping paints the image from a per-pointer draft outside the document,
    // and animations move every frame; neither is worth caching.
    const reusable =
      !animating &&
      state.croppingId === null &&
      this.boardLayer !== null &&
      sameInputs(this.boardInputs, inputs);

    const visible: BoardNode[] = reusable ? this.lastVisible : [];
    if (reusable) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.drawImage(this.boardLayer!, 0, 0);
    } else {
      this.paintBoard(ctx, vp, meta, state, nodes, now, visible);
      this.storeBoardLayer(animating || state.croppingId !== null ? null : inputs);
      this.lastVisible = visible;
    }

    ctx.save();
    ctx.setTransform(
      this.dpr * vp.zoom,
      0,
      0,
      this.dpr * vp.zoom,
      -vp.x * vp.zoom * this.dpr,
      -vp.y * vp.zoom * this.dpr,
    );
    this.paintLiveStroke(ctx);
    ctx.restore();

    // ---- overlay (screen space) -------------------------------------------
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    const selected = this.doc.getMany(state.selection);
    const hover = scratch.hoverId ? this.doc.get(scratch.hoverId) : undefined;

    if (hover && !state.selection.includes(hover.id) && !scratch.gestureActive) {
      paintHover(ctx, hover, vp, this.doc.lookup);
    }
    for (const id of scratch.highlightIds) {
      const node = this.doc.get(id);
      if (node) paintHighlight(ctx, node, vp, this.doc.lookup);
    }

    if (selected.length > 0 && state.editingId === null && state.croppingId === null) {
      const frame: SelectionFrame = {
        bounds: selectionBounds(selected, this.doc.lookup),
        rotation: selectionRotation(selected),
      };
      // A lone connector shows endpoint grips instead of a box and handles:
      // its bounding box is padded and would read as a phantom rectangle.
      const onlyConnector = selected.length === 1 && selected[0]!.type === 'connector';
      if (!onlyConnector) {
        paintMemberOutlines(ctx, selected, vp, this.doc.lookup);
        paintSelectionOutline(ctx, frame, vp);
      }
      if (onlyConnector) {
        paintConnectorGrips(ctx, selected[0]!, vp, this.doc.lookup);
      } else if (!selected.some((n) => n.locked)) {
        const resize = !selected.every((n) => n.type === 'notebook');
        paintHandles(ctx, frame, vp, { resize });
      }
    }

    if (state.croppingId) {
      const image = this.doc.get(state.croppingId);
      if (image?.type === 'image') {
        const crop = scratch.cropDraft ?? image.crop ?? { x: 0, y: 0, w: 1, h: 1 };
        // The full image's box comes from the saved crop; `crop` (the draft)
        // is only the window drawn inside it.
        const saved = image.crop ?? { x: 0, y: 0, w: 1, h: 1 };
        const scaleX = image.w / Math.max(saved.w * image.naturalW, 1e-6);
        const scaleY = image.h / Math.max(saved.h * image.naturalH, 1e-6);
        const source: Rect = {
          x: image.x - saved.x * image.naturalW * scaleX,
          y: image.y - saved.y * image.naturalH * scaleY,
          w: image.naturalW * scaleX,
          h: image.naturalH * scaleY,
        };
        const mask =
          scratch.cropMaskDraft && scratch.cropMaskDraft.length >= 3
            ? scratch.cropMaskDraft
            : image.mask;
        paintCropOverlay(ctx, source, crop, vp, {
          mask,
          livePath: scratch.cropPath,
          rotation:
            image.rotation !== 0
              ? {
                  center: rectCenter({ x: image.x, y: image.y, w: image.w, h: image.h }),
                  angle: image.rotation,
                }
              : null,
        });
      }
    }

    if (scratch.marquee) paintMarquee(ctx, scratch.marquee, vp);
    if (scratch.creationPreview) {
      paintCreationPreview(ctx, scratch.creationPreview, vp, state.shapeKind);
    }
    if (scratch.connectorPreview) paintConnectorPreview(ctx, scratch.connectorPreview, vp);
    if (scratch.anchorHint) {
      const node = this.doc.get(scratch.anchorHint.nodeId);
      if (node) paintAnchors(ctx, node, vp, scratch.anchorHint.point);
    } else if (hover && state.tool === 'connector' && hover.type !== 'connector') {
      paintAnchors(ctx, hover, vp, null);
    }
    if (scratch.guides.length > 0) paintGuides(ctx, scratch.guides, vp);
    if (scratch.eraserPoint) paintEraser(ctx, scratch.eraserPoint, scratch.eraserRadius, vp);
    if (scratch.brushCursorPoint && state.tool === 'draw') {
      paintBrushCursor(
        ctx,
        scratch.brushCursorPoint,
        state.drawStyle.brush,
        state.drawStyle.color,
        state.drawStyle.size,
        vp,
      );
    }

    const peers = this.doc.getPresence();
    this.peerCursors.sync(peers, now);
    const cursorsMoving = this.peerCursors.tick(now);
    this.peerDrafts.sync(peers, now);
    const draftsGrowing = this.peerDrafts.tick(now);

    this.peerDrafts.paint(ctx, vp);
    this.peerCursors.paintSelections(ctx, vp, this.doc.lookup);
    paintPresenceViewports(ctx, peers, vp, state.followingUserId);
    this.peerCursors.paintCursors(ctx, vp, now);

    if (scratch.lasers.size > 0) {
      paintLasers(ctx, scratch.lasers, vp, now);
      // Fading trails need another frame until they are gone.
      pruneLasers(now);
      this.invalidate();
    }

    // Anything still in motion asks for the next frame itself, so the loop
    // stays idle whenever the board is at rest.
    if (cursorsMoving || draftsGrowing || animations.active()) this.invalidate();

    this.lastVisibleCount = visible.length;
  }

  /** Background, grid and every node on screen, into the main canvas. */
  private paintBoard(
    ctx: CanvasRenderingContext2D,
    vp: Viewport,
    meta: BoardMeta,
    state: ReturnType<typeof editorState>,
    nodes: readonly BoardNode[],
    now: number,
    visible: BoardNode[],
  ): void {
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.fillStyle = meta.background;
    ctx.fillRect(0, 0, vp.width, vp.height);

    if (state.showGrid) paintGrid(ctx, vp, meta.grid, meta.gridSize);

    const env: PaintEnv = {
      lookup: this.doc.lookup,
      zoom: vp.zoom,
      editingId: state.editingId,
      croppingId: state.croppingId,
      enteredNotebookId: state.enteredNotebookId,
    };
    // A generous margin keeps shadows and connector arrowheads from popping in
    // at the edge of the screen.
    const cull = rectExpand(visibleRect(vp), 240 / vp.zoom);

    ctx.save();
    ctx.setTransform(
      this.dpr * vp.zoom,
      0,
      0,
      this.dpr * vp.zoom,
      -vp.x * vp.zoom * this.dpr,
      -vp.y * vp.zoom * this.dpr,
    );

    for (const node of nodes) {
      if (node.type === 'group' || node.hidden) continue;
      const aabb = nodeAABB(node, this.doc.lookup);
      if (!rectIntersects(cull, aabb)) continue;
      visible.push(node);

      const anim = animations.stateOf(node.id, now);
      if (!anim) {
        paintNode(ctx, node, env);
        continue;
      }
      if (anim.kind === 'draw') {
        paintNode(ctx, node, { ...env, reveal: anim.linear });
        continue;
      }
      // Entrance: fade up while scaling from just under full size, about the
      // object's own centre so it appears to settle in place.
      const c = rectCenter(aabb);
      const scale = 0.92 + 0.08 * anim.t;
      ctx.save();
      ctx.globalAlpha *= anim.t;
      ctx.translate(c.x, c.y);
      ctx.scale(scale, scale);
      ctx.translate(-c.x, -c.y);
      paintNode(ctx, node, env);
      ctx.restore();
    }

    // Deleted objects keep painting for a moment so removal has a direction.
    for (const ghost of animations.ghosts(now)) {
      const aabb = nodeAABB(ghost.node, this.doc.lookup);
      const c = rectCenter(aabb);
      const scale = 1 - 0.08 * ghost.t;
      ctx.save();
      ctx.globalAlpha *= 1 - ghost.t;
      ctx.translate(c.x, c.y);
      ctx.scale(scale, scale);
      ctx.translate(-c.x, -c.y);
      paintNode(ctx, ghost.node, env);
      ctx.restore();
    }

    ctx.restore();
  }

  /**
   * Copies the freshly painted board aside for the frames that follow, or
   * drops the copy when `inputs` is null because the next frame repaints
   * anyway.
   */
  private storeBoardLayer(inputs: unknown[] | null): void {
    if (!inputs) {
      this.boardInputs = [];
      return;
    }
    const { width, height } = this.canvas;
    let layer = this.boardLayer;
    if (!layer) {
      layer = document.createElement('canvas');
      this.boardLayer = layer;
    }
    if (layer.width !== width || layer.height !== height) {
      layer.width = width;
      layer.height = height;
    }
    const lctx = layer.getContext('2d', { alpha: false });
    if (!lctx) {
      this.boardInputs = [];
      return;
    }
    lctx.setTransform(1, 0, 0, 1, 0, 0);
    lctx.drawImage(this.canvas, 0, 0);
    this.boardInputs = inputs;
  }

  /** Nodes the last full board paint found on screen. */
  private lastVisible: BoardNode[] = [];

  /** Node count painted in the last frame, surfaced in the debug readout. */
  lastVisibleCount = 0;

  /**
   * The stroke in progress is painted straight from scratch state rather than
   * being written to the document on every sample. Committing to the CRDT at
   * pointer rate would flood every peer with updates for a shape that is not
   * final yet.
   */
  private paintLiveStroke(ctx: CanvasRenderingContext2D): void {
    const points = scratch.drawPoints;
    if (!points || points.length === 0) return;

    ctx.save();
    if (scratch.drawBrush === 'highlighter') {
      ctx.globalAlpha = 0.4;
      ctx.globalCompositeOperation = 'multiply';
    }
    const preset = BRUSH_PRESETS[scratch.drawBrush] ?? {};
    const opts = { ...DEFAULT_STROKE_OPTIONS, ...preset, size: scratch.drawSize };

    // The same outline the committed stroke will get, pressure included, so a
    // pen stroke does not pop from a uniform ribbon into a tapered one when
    // the nib lifts. Very long strokes fall back to a centre line: the
    // outline is linear in the sample count, but so is the frame budget.
    if (points.length > 1 && points.length <= LIVE_OUTLINE_MAX_POINTS) {
      const outline = strokeOutline(points, opts);
      if (outline.length > 2) {
        ctx.fillStyle = scratch.drawColor;
        ctx.beginPath();
        ctx.moveTo(outline[0]!.x, outline[0]!.y);
        for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i]!.x, outline[i]!.y);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        return;
      }
    }

    // Same centre-line prepare as commit, so handwriting does not pop from a
    // faceted polyline into a smoothed outline when the pointer lifts.
    const thinning = preset.thinning ?? DEFAULT_STROKE_OPTIONS.thinning;
    const pressure = averagePressure(points);
    ctx.strokeStyle = scratch.drawColor;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = scratch.drawSize * (1 - thinning + thinning * pressure);

    const line = strokeCenterline(points, opts);
    if (line.length > 1) {
      ctx.beginPath();
      ctx.moveTo(line[0]!.x, line[0]!.y);
      for (let i = 1; i < line.length; i++) ctx.lineTo(line[i]!.x, line[i]!.y);
      ctx.stroke();
    } else if (line.length === 1) {
      const p = line[0]!;
      const r = ctx.lineWidth / 2;
      ctx.fillStyle = scratch.drawColor;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  /** Rect covering every node, used by fit-to-content and the minimap. */
  contentBounds(): Rect | null {
    const nodes = this.doc.all();
    if (nodes.length === 0) return null;
    let out: Rect | null = null;
    for (const node of nodes) {
      if (node.type === 'group' || node.hidden) continue;
      const r = nodeAABB(node, this.doc.lookup);
      out = out
        ? {
            x: Math.min(out.x, r.x),
            y: Math.min(out.y, r.y),
            w: Math.max(out.x + out.w, r.x + r.w) - Math.min(out.x, r.x),
            h: Math.max(out.y + out.h, r.y + r.h) - Math.min(out.y, r.y),
          }
        : r;
    }
    return out;
  }
}

/**
 * Arc length of a connector's resolved route, so its reveal animation runs at
 * the same apparent speed as a freehand stroke of the same length.
 */
function connectorLength(node: BoardNode, doc: BoardDoc): number {
  const points = connectorGeometry(node as never, doc.lookup).points;
  let length = 0;
  for (let i = 1; i < points.length; i++) length += vDist(points[i - 1]!, points[i]!);
  return length;
}

/** Store subscription outside React, so a camera change repaints directly. */
function editorSubscribe(listener: () => void): () => void {
  return useEditor.subscribe(listener);
}

function sameInputs(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
